import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { isDefined, isNil } from '@trimble-gcs/common';
import { ModusButtonModule, ModusIconModule } from '@trimble-gcs/modus';
import { PatchScandataModels } from '../../scandata/scandata.actions';
import { ScandataState } from '../../scandata/scandata.state';
import { Scan3dStyle } from '../models/scan-3d-style';
import { Scan3dStyleRenderingMode } from '../models/scan-3d-style-rendering-mode';
import { Scan3dStyleUom } from '../models/scan-3d-style-uom';
import { Scan3dStylingComponent } from '../scan-3d-styling/scan-3d-styling.component';
import { ElevationPicked, SetGlobalStyle } from '../scan-3d.actions';
import { Scan3dService } from '../scan-3d.service';
import { Scan3dState } from '../scan-3d.state';
import { scan3dSettingsTranslations } from './scan-3d-settings.translations';

@UntilDestroy()
@Component({
  selector: 'sd-scan-3d-settings',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModusButtonModule,
    ModusIconModule,
    MatProgressBarModule,
    Scan3dStylingComponent,
  ],
  templateUrl: './scan-3d-settings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Scan3dSettingsComponent {
  private readonly actions = inject(Actions);
  private readonly store = inject(Store);
  private readonly scan3dService = inject(Scan3dService);

  readonly scan3dStyleUom = input.required<Scan3dStyleUom>();
  readonly elevationPicking = input<boolean>(false);

  readonly exitSettingsClick = output();
  readonly elevationPickingChange = output<boolean>();

  readonly scan3dStyle = signal<Scan3dStyle | undefined>(undefined);

  readonly isLoading = toSignal(this.store.select(ScandataState.isLoading), {
    initialValue: false,
  });

  readonly translations = scan3dSettingsTranslations();

  constructor() {
    this.subscribeToElevationPicked();
    this.subscribeToScan3dStyle();
  }

  onStyleChange(style: Scan3dStyle) {
    // Turn eye dome lighting on when classification color is switched on
    const globalStyle = this.store.selectSnapshot(Scan3dState.globalStyle);

    const classificationShowing =
      globalStyle?.renderingMode === Scan3dStyleRenderingMode.Classification;

    if (
      classificationShowing === false &&
      style.renderingMode === Scan3dStyleRenderingMode.Classification &&
      style.showEyeDomeLighting === false
    ) {
      style.showEyeDomeLighting = true;
    }

    this.updateStyleState(style);
  }

  onElevationPickingChange(picking: boolean) {
    this.elevationPickingChange.emit(picking);
  }

  private subscribeToElevationPicked() {
    this.actions
      .pipe(ofActionDispatched(ElevationPicked), untilDestroyed(this))
      .subscribe(({ elevationPicked }) => {
        const globalStyle = this.store.selectSnapshot(Scan3dState.globalStyle);
        if (isNil(globalStyle)) return;

        const style = { ...globalStyle, elevationReference: elevationPicked };
        this.scan3dStyle.set(style);
        this.updateStyleState(style);
      });
  }

  private subscribeToScan3dStyle() {
    this.scan3dService.getGlobalStyle().subscribe((globalStyle) => {
      this.scan3dStyle.set(globalStyle);
    });
  }

  private updateStyleState(style: Scan3dStyle) {
    const scans = this.store
      .selectSnapshot(ScandataState.scandata)
      .filter((scan) => isDefined(scan.scan3dStyle))
      .map((scan) => {
        return { ...scan, scan3dStyle: undefined };
      });

    this.store.dispatch([new SetGlobalStyle(style), new PatchScandataModels(scans)]);
  }
}
